import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Fingerprint, CheckCircle2, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';
import HealthLoopLogo from '@/components/HealthLoopLogo';
import EmbeddedBrowserBanner from '@/components/EmbeddedBrowserBanner';
import AppleSignInButton from '@/components/AppleSignInButton';
import { detectEmbeddedBrowser } from '@/utils/embeddedBrowser';
import { isWebAuthnSupported, isPlatformAuthenticatorAvailable, registerPasskey } from '@/utils/webauthn';
import { useLanguage } from '@/contexts/LanguageContext';

const EMBEDDED = detectEmbeddedBrowser();

const Register = () => {
  const { t } = useLanguage();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showBiometricSetup, setShowBiometricSetup] = useState(false);
  const [biometricAvailable, setBiometricAvailable] = useState(false);
  const [registeredUser, setRegisteredUser] = useState(null);
  const { register, user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    checkBiometricAvailability();
  }, []);

  const checkBiometricAvailability = async () => {
    if (isWebAuthnSupported()) {
      const available = await isPlatformAuthenticatorAvailable();
      setBiometricAvailable(available);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    if (password.length < 8) {
      setError(t('register.passwordTooShort'));
      setLoading(false);
      return;
    }

    const result = await register(email, password, name);
    setLoading(false);

    if (result.success) {
      setRegisteredUser(result.user);
      
      // If biometrics available, show setup prompt
      if (biometricAvailable) {
        setShowBiometricSetup(true);
      } else {
        navigate('/dashboard');
      }
    } else {
      setError(result.error);
    }
  };

  const handleSetupBiometric = async () => {
    setLoading(true);
    try {
      await registerPasskey(registeredUser.id, registeredUser.email, registeredUser.name);
      toast.success(t('register.biometricSuccess'));
      navigate('/dashboard');
    } catch (error) {
      console.error('Biometric setup error:', error);
      toast.error(t('register.biometricFailed'));
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  };

  const handleSkipBiometric = () => {
    toast.info(t('register.biometricSetupLater'));
    navigate('/dashboard');
  };

  if (showBiometricSetup) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-healthloop-mint-100 via-healthloop-mint-200 to-healthloop-mint-300 p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="flex justify-center mb-4">
              <div className="w-16 h-16 bg-healthloop-mint-100 rounded-full flex items-center justify-center">
                <Fingerprint className="h-8 w-8 text-healthloop-mint-600" />
              </div>
            </div>
            <CardTitle className="text-2xl font-bold text-healthloop-navy-900">
              {t('register.setupBiometric')}
            </CardTitle>
            <CardDescription>
              {t('register.setupBiometricDesc')}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="bg-healthloop-mint-50 border border-healthloop-mint-200 rounded-lg p-4">
              <div className="space-y-2">
                <div className="flex items-start gap-2">
                  <CheckCircle2 className="h-4 w-4 text-healthloop-mint-600 mt-0.5" />
                  <p className="text-sm text-gray-700">{t('register.benefit1')}</p>
                </div>
                <div className="flex items-start gap-2">
                  <CheckCircle2 className="h-4 w-4 text-healthloop-mint-600 mt-0.5" />
                  <p className="text-sm text-gray-700">{t('register.benefit2')}</p>
                </div>
                <div className="flex items-start gap-2">
                  <CheckCircle2 className="h-4 w-4 text-healthloop-mint-600 mt-0.5" />
                  <p className="text-sm text-gray-700">{t('register.benefit3')}</p>
                </div>
              </div>
            </div>

            <Button
              onClick={handleSetupBiometric}
              disabled={loading}
              className="w-full bg-healthloop-mint-600 hover:bg-healthloop-mint-700 text-white"
            >
              <Fingerprint className="h-4 w-4 mr-2" />
              {loading ? t('register.settingUp') : t('register.setupNow')}
            </Button>

            <Button
              onClick={handleSkipBiometric}
              variant="ghost"
              className="w-full"
            >
              {t('register.skipForNow')}
            </Button>

            <p className="text-xs text-center text-gray-500">
              {t('register.biometricPrivacy')}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-healthloop-mint-100 via-healthloop-mint-200 to-healthloop-mint-300 p-4">
      <Card className="w-full max-w-md" data-testid="register-card">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <HealthLoopLogo size="lg" showText={true} />
          </div>
          <CardTitle className="text-2xl font-bold text-healthloop-navy-900">{t('register.title')}</CardTitle>
          <CardDescription>{t('register.subtitle')}</CardDescription>
        </CardHeader>
        <CardContent>
          {/* Embedded-WebView guard — see Login.jsx for full rationale.
              Google's "Use Secure Browsers" policy blocks OAuth from
              in-app browsers (Instagram, FB, LinkedIn, etc.). */}
          {EMBEDDED.isEmbedded && <EmbeddedBrowserBanner label={EMBEDDED.label} />}

          {/* Continue with Google — primary social CTA above email/password.
              REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR
              REDIRECT URLS, THIS BREAKS THE AUTH. */}
          <button
            type="button"
            disabled={EMBEDDED.isEmbedded}
            onClick={() => {
              if (EMBEDDED.isEmbedded) return;
              const redirectUrl = window.location.origin + '/dashboard';
              window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
            }}
            className={`w-full h-12 mb-4 flex items-center justify-center gap-3 rounded-xl border bg-white transition-all duration-200 text-slate-700 font-medium shadow-sm ${
              EMBEDDED.isEmbedded
                ? 'border-slate-200 opacity-50 cursor-not-allowed'
                : 'border-slate-200 hover:bg-slate-50 active:scale-[0.97] hover:shadow'
            }`}
            data-testid="register-continue-with-google-button"
            aria-label={t('auth.continueWithGoogle')}
            aria-disabled={EMBEDDED.isEmbedded}
          >
            <svg className="h-5 w-5" viewBox="0 0 48 48" aria-hidden="true">
              <path fill="#FFC107" d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z"/>
              <path fill="#FF3D00" d="M6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 16.318 4 9.656 8.337 6.306 14.691z"/>
              <path fill="#4CAF50" d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238C29.211 35.091 26.715 36 24 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z"/>
              <path fill="#1976D2" d="M43.611 20.083H42V20H24v8h11.303c-.792 2.237-2.231 4.166-4.087 5.571.001-.001.002-.001.003-.002l6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z"/>
            </svg>
            <span>{t('auth.continueWithGoogle')}</span>
          </button>

          {/* Continue with Apple — see Login.jsx. Renders nothing until
              the operator flips ENABLE_APPLE_SIGNIN=true on production. */}
          <AppleSignInButton testIdPrefix="register-" />

          <div className="flex items-center gap-3 mb-4" aria-hidden="true">
            <div className="flex-1 h-px bg-slate-200" />
            <span className="text-xs uppercase tracking-wide text-slate-400">{t('auth.orDivider')}</span>
            <div className="flex-1 h-px bg-slate-200" />
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <div className="bg-red-50 text-red-600 p-3 rounded-md text-sm" data-testid="register-error">
                {error}
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="name">{t('register.fullName')}</Label>
              <Input
                id="name"
                type="text"
                placeholder="John Doe"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                data-testid="name-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">{t('register.email')}</Label>
              <Input
                id="email"
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                data-testid="email-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">{t('register.password')}</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={8}
                  className="pr-12"
                  data-testid="password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center justify-center h-9 w-9 text-gray-400 hover:text-gray-600 rounded-md transition-colors"
                  aria-label={showPassword ? t('auth.hidePassword') : t('auth.showPassword')}
                  aria-pressed={showPassword}
                  tabIndex={-1}
                  data-testid="register-password-toggle"
                >
                  {showPassword ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                </button>
              </div>
            </div>
            <Button
              type="submit"
              className="w-full bg-healthloop-mint-600 hover:bg-healthloop-mint-700 text-white"
              disabled={loading}
              data-testid="register-submit-button"
            >
              {loading ? t('register.creating') : t('register.createAccount')}
            </Button>
          </form>
          <div className="mt-6 text-center text-sm">
            <p className="text-gray-600">
              {t('register.haveAccount')}{' '}
              <Link to="/login" className="text-healthloop-mint-700 hover:text-healthloop-mint-800 font-semibold">
                {t('register.signIn')}
              </Link>
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Register;
